import React, { useEffect, useState, useCallback } from 'react';
import {
  MapPin, Building2, Bed, Wifi, Search, X, ExternalLink,
  RefreshCw, ChevronDown, ChevronRight, Globe2, Star,
  Layers, ShieldCheck, Utensils, CreditCard, Home, Download, Filter, RotateCcw
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { propertyAnalyticsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const CHART_COLORS = ['#0d9488', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'];

// ─── Sub-Components ──────────────────────────

const KpiCard = ({ title, value, icon: Icon, color, sub }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-lg transition-shadow" data-testid={`geo-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className={`p-2.5 rounded-xl ${color}`}><Icon size={20} className="text-white" /></div>
      <div>
        <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
      </div>
    </div>
  </div>
);

const Section = ({ id, title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`geo-section-${id}`}>
      <button onClick={() => setOpen(p => !p)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors">
        <Icon size={20} className="text-teal-600 flex-shrink-0" />
        <span className="font-semibold text-gray-800 flex-1">{title}</span>
        {open ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-gray-50">{children}</div>}
    </div>
  );
};

const PieCard = ({ data, title, onItemClick }) => {
  const items = data.filter(d => d.count > 0);
  if (!items.length) return <p className="text-sm text-gray-400 py-4 text-center">No data</p>;
  const total = items.reduce((s, d) => s + d.count, 0);
  return (
    <div>
      <p className="text-sm font-medium text-gray-700 mb-3">{title}</p>
      <div className="flex items-start gap-4">
        <ResponsiveContainer width={130} height={130}>
          <PieChart><Pie data={items} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={55} innerRadius={25}>
            {items.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Pie><Tooltip formatter={v => v.toLocaleString()} /></PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-1 max-h-[130px] overflow-y-auto">
          {items.map((item, i) => (
            <button key={item.name} onClick={() => onItemClick?.(item)} className="flex items-center gap-2 w-full text-left hover:bg-gray-50 rounded px-1.5 py-0.5 transition-colors group" data-testid={`geo-pie-${title}-${item.name}`}>
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
              <span className="text-xs text-gray-600 flex-1 truncate group-hover:text-gray-900">{item.name}</span>
              <span className="text-xs font-semibold text-gray-800">{item.count}</span>
              <span className="text-[10px] text-gray-400">({total > 0 ? Math.round((item.count / total) * 100) : 0}%)</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const BarCard = ({ data, title, onItemClick, layout = 'horizontal', height = 200, color = '#0d9488' }) => {
  const items = data.filter(d => d.count > 0).slice(0, 20);
  if (!items.length) return <p className="text-sm text-gray-400 py-4 text-center">No data</p>;
  return (
    <div>
      <p className="text-sm font-medium text-gray-700 mb-3">{title}</p>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={items} layout={layout === 'vertical' ? 'vertical' : 'horizontal'}
          onClick={e => { if (e?.activeLabel) { const it = items.find(d => d.name === e.activeLabel); if (it) onItemClick?.(it); } }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          {layout === 'vertical' ? (
            <><XAxis type="number" tick={{ fontSize: 10 }} /><YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} /></>
          ) : (
            <><XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={55} /><YAxis tick={{ fontSize: 10 }} /></>
          )}
          <Tooltip formatter={v => v.toLocaleString()} />
          <Bar dataKey="count" fill={color} radius={layout === 'vertical' ? [0, 4, 4, 0] : [4, 4, 0, 0]} cursor="pointer" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const BoolStat = ({ data, title, onItemClick }) => {
  const yes = data.find(d => d.name === 'Yes')?.count || 0;
  const no = data.find(d => d.name === 'No')?.count || 0;
  const total = yes + no;
  const pct = total > 0 ? Math.round((yes / total) * 100) : 0;
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <p className="text-xs font-medium text-gray-600 mb-2">{title}</p>
      <div className="flex items-end gap-2 mb-2">
        <span className="text-2xl font-bold text-gray-900">{pct}%</span>
        <span className="text-xs text-gray-500 mb-1">{yes} / {total}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2"><div className="bg-teal-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
      <div className="flex gap-2 mt-2">
        <button onClick={() => onItemClick?.({ name: 'Yes', count: yes })} className="text-[11px] text-teal-600 hover:underline">Yes: {yes}</button>
        <button onClick={() => onItemClick?.({ name: 'No', count: no })} className="text-[11px] text-gray-500 hover:underline">No: {no}</button>
      </div>
    </div>
  );
};

const NumStat = ({ label, value, sub }) => (
  <div className="bg-gray-50 rounded-lg p-3 text-center">
    <p className="text-xl font-bold text-gray-900">{typeof value === 'number' ? (Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1)) : value || '-'}</p>
    <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
  </div>
);

// ─── Analytics key → display column mapping for dynamic drill-down ──────
const GEO_DISPLAY_MAP = {
  state: { label: 'State', key: 'state' },
  city: { label: 'City', key: 'city' },
  property_type: { label: 'Property Type', key: 'propertyType' },
  property_status: { label: 'Property Status', key: 'propertyStatus' },
  star_rating: { label: 'Star Rating', key: 'starRating' },
  onboarding_status: { label: 'Onboarding Status', key: 'onboardingStatus' },
  cancellation_policy: { label: 'Cancellation Policy', key: 'cancellationPolicy' },
  amenity: { label: 'Amenity', key: '_static' },
  facility: { label: 'Facility', key: '_static' },
  room_type: { label: 'Room Type', key: '_static' },
  ac_status: { label: 'AC Status', key: '_static' },
  has_bank_yes: { label: 'Has Bank Details', key: '_static', staticValue: 'Yes' },
  has_bank_no: { label: 'Has Bank Details', key: '_static', staticValue: 'No' },
  has_gst_yes: { label: 'Has GST', key: '_static', staticValue: 'Yes' },
  has_gst_no: { label: 'Has GST', key: '_static', staticValue: 'No' },
};

function getGeoFieldValue(prop, mapKey, filterValue) {
  const display = GEO_DISPLAY_MAP[mapKey];
  if (!display) return filterValue || '-';
  if (display.key === '_static') return display.staticValue || filterValue || '-';
  return prop[display.key] || '-';
}

function geoExportToExcel(data, colLabel, colAccessor, filename) {
  const rows = data.map(item => ({
    'Property Name': item.propertyName || '-',
    [colLabel]: colAccessor(item),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
}

// ─── Drilldown Panel ──────────────────────────
const DrilldownPanel = ({ isOpen, data, title, subtitle, onClose, onFullView, loading, analyticsKey, filterValue }) => {
  const [search, setSearch] = useState('');
  if (!isOpen) return null;

  const display = GEO_DISPLAY_MAP[analyticsKey] || { label: 'Value', key: '_static' };
  const colLabel = display.label;
  const getValue = (p) => getGeoFieldValue(p, analyticsKey, filterValue);

  const filtered = (data || []).filter(l => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.propertyName || '').toLowerCase().includes(q) || String(getValue(l)).toLowerCase().includes(q);
  });

  const handleExport = () => {
    geoExportToExcel(filtered, colLabel, (item) => getGeoFieldValue(item, analyticsKey, filterValue), `properties_${analyticsKey}_${filterValue || 'all'}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="geo-drilldown-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div>
            <h3 className="font-semibold text-gray-800 text-base">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors" data-testid="geo-drilldown-export-excel"><Download size={13} /> Export Excel</button>
            <button onClick={onFullView} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors" data-testid="geo-drilldown-full-view"><ExternalLink size={13} /> Open Full View</button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg" data-testid="geo-drilldown-close"><X size={18} className="text-gray-500" /></button>
          </div>
        </div>
        <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search properties..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none" data-testid="geo-drilldown-search" />
          </div>
          <p className="text-xs text-gray-400 mt-1.5">{filtered.length} of {(data || []).length} properties</p>
        </div>
        <div className="flex-1 overflow-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400"><Search size={32} className="mx-auto mb-2 text-gray-300" /><p>No matching properties</p></div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 w-12">#</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Property Name</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">{colLabel}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((p, i) => (
                  <tr key={p.id || i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-xs text-gray-400">{i + 1}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{p.propertyName || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-600">{getValue(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Filter-to-drilldown mapping ──────────────────────────
const DRILLDOWN_MAP = {
  state: { field: 'state', op: 'eq' },
  city: { field: 'city', op: 'eq' },
  property_type: { field: 'propertyType', op: 'eq' },
  property_status: { field: 'propertyStatus', op: 'eq' },
  star_rating: { field: 'starRating', op: 'eq' },
  onboarding_status: { field: 'onboardingStatus', op: 'eq' },
  cancellation_policy: { field: 'cancellationPolicy', op: 'eq' },
  amenity: { field: '__amenity__', op: 'has_amenity' },
  facility: { field: '__facility__', op: 'has_facility' },
  room_type: { field: 'rooms.category', op: 'eq' },
  ac_status: { field: 'rooms.roomTypeCategory', op: 'eq' },
  has_bank_yes: { field: 'banking.bankName', op: 'exists' },
  has_bank_no: { field: 'banking.bankName', op: 'not_exists' },
  has_gst_yes: { field: 'banking.gstNumber', op: 'exists' },
  has_gst_no: { field: 'banking.gstNumber', op: 'not_exists' },
};

// ─── Geo Filter Bar ──────────────────────────
const GeoMultiSelect = ({ label, options, selected, onChange, searchable = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = React.useRef(null);
  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const filtered = options.filter(o => !q || o.toLowerCase().includes(q.toLowerCase()));
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-xs border border-gray-200 rounded-lg hover:border-gray-300 bg-white" data-testid={`geo-filter-${label}`}>
        <span className="truncate text-gray-700">{selected.length > 0 ? `${label} (${selected.length})` : label}</span>
        <ChevronDown size={13} className="text-gray-400 ml-1 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
          {searchable && (
            <div className="p-2 border-b border-gray-100">
              <input type="text" placeholder="Search..." value={q} onChange={e => setQ(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-teal-400 outline-none" />
            </div>
          )}
          <div className="max-h-48 overflow-y-auto p-1">
            {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No options</p>}
            {filtered.map(opt => (
              <label key={opt} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 h-3.5 w-3.5" />
                <span className="text-xs text-gray-700 truncate">{opt}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && <div className="p-2 border-t border-gray-100"><button onClick={() => onChange([])} className="text-[10px] text-red-500 hover:underline">Clear</button></div>}
        </div>
      )}
    </div>
  );
};

const GeoFilterBar = ({ filters, onChange, onApply, onReset, filterOptions, activeCount }) => {
  const set = (key, val) => onChange({ ...filters, [key]: val });
  const opts = filterOptions || {};
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="geo-filter-bar">
      <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
        <GeoMultiSelect label="State" options={opts.state || []} selected={filters.state || []} onChange={v => set('state', v)} searchable />
        <GeoMultiSelect label="City" options={opts.city || []} selected={filters.city || []} onChange={v => set('city', v)} searchable />
        <GeoMultiSelect label="Property Type" options={opts.property_type || []} selected={filters.property_type || []} onChange={v => set('property_type', v)} />
        <GeoMultiSelect label="Property Status" options={opts.property_status || []} selected={filters.property_status || []} onChange={v => set('property_status', v)} />
        <button onClick={onApply} className="px-3 py-2 text-xs font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors" data-testid="geo-filter-apply">
          <Filter size={13} className="inline mr-1" />Apply
        </button>
        {activeCount > 0 && (
          <button onClick={onReset} className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-red-500 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors" data-testid="geo-filter-reset">
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>
      {activeCount > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-3">
          {(filters.state || []).map(v => (
            <span key={`s-${v}`} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-teal-50 text-teal-700 rounded-md">
              State: {v} <button onClick={() => { set('state', (filters.state || []).filter(x => x !== v)); onApply(); }}><X size={11} /></button>
            </span>
          ))}
          {(filters.city || []).map(v => (
            <span key={`c-${v}`} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-blue-50 text-blue-700 rounded-md">
              City: {v} <button onClick={() => { set('city', (filters.city || []).filter(x => x !== v)); onApply(); }}><X size={11} /></button>
            </span>
          ))}
          {(filters.property_type || []).map(v => (
            <span key={`pt-${v}`} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-violet-50 text-violet-700 rounded-md">
              Type: {v} <button onClick={() => { set('property_type', (filters.property_type || []).filter(x => x !== v)); onApply(); }}><X size={11} /></button>
            </span>
          ))}
          {(filters.property_status || []).map(v => (
            <span key={`ps-${v}`} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium bg-orange-50 text-orange-700 rounded-md">
              Status: {v} <button onClick={() => { set('property_status', (filters.property_status || []).filter(x => x !== v)); onApply(); }}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────
const EMPTY_GEO_FILTERS = { state: [], city: [], property_type: [], property_status: [] };

const GeoPropertyAnalytics = ({ crossFilters = {}, onCrossFilterToggle }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dd, setDd] = useState({ open: false, data: [], title: '', subtitle: '', loading: false, key: '', val: '' });
  const [filters, setFilters] = useState({ ...EMPTY_GEO_FILTERS });
  const [filterOptions, setFilterOptions] = useState({});

  const activeFilterCount = [
    ...(filters.state || []),
    ...(filters.city || []),
    ...(filters.property_type || []),
    ...(filters.property_status || []),
  ].length;

  // Merge local filters with cross-filters for the API call
  const mergeFilters = useCallback((localFilters, cf) => {
    const merged = { ...localFilters };
    const cfState = cf?.state || [];
    const cfCity = cf?.city || [];
    if (cfState.length > 0) {
      merged.state = [...new Set([...(merged.state || []), ...cfState])];
    }
    if (cfCity.length > 0) {
      merged.city = [...new Set([...(merged.city || []), ...cfCity])];
    }
    return merged;
  }, []);

  const load = useCallback(async (f, cf) => {
    try {
      setLoading(true);
      const currentFilters = f || filters;
      const currentCross = cf !== undefined ? cf : crossFilters;
      const merged = mergeFilters(currentFilters, currentCross);
      const hasFilters = Object.values(merged).some(v => Array.isArray(v) && v.length > 0);
      const res = hasFilters
        ? await propertyAnalyticsAPI.getFiltered(merged)
        : await propertyAnalyticsAPI.getComprehensive();
      setData(res.data);
      // Extract filter options from unfiltered data
      if (!hasFilters && res.data?.filter_options) {
        setFilterOptions(res.data.filter_options);
      }
    } catch (err) {
      console.error('Failed to load property analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, crossFilters, mergeFilters]);

  // Initial load
  useEffect(() => { load(); }, []);

  // Re-load when cross-filters change
  useEffect(() => { load(filters, crossFilters); }, [crossFilters]);

  const applyFilters = useCallback(() => { load(filters, crossFilters); }, [filters, crossFilters, load]);
  const resetFilters = useCallback(() => {
    const empty = { ...EMPTY_GEO_FILTERS };
    setFilters(empty);
    load(empty, crossFilters);
  }, [load, crossFilters]);

  // Chart click → toggle cross-filter + open drilldown
  const handleChartCrossFilter = useCallback((crossKey, item, mapKey, sectionLabel) => {
    if (onCrossFilterToggle && item.name && item.name !== 'Not Set') {
      onCrossFilterToggle(crossKey, item.name);
    }
    openDd(mapKey, item, sectionLabel);
  }, [onCrossFilterToggle]);

  const openDd = useCallback(async (mapKey, item, sectionLabel) => {
    const mapping = DRILLDOWN_MAP[mapKey];
    if (!mapping) return;
    const val = item.name === 'Not Set' ? '' : String(item.name);

    setDd({ open: true, data: [], title: `${sectionLabel}: ${item.name}`, subtitle: `${item.count} properties`, loading: true, key: mapKey, val: item.name });

    try {
      let op = mapping.op;
      let field = mapping.field;
      let queryValue = val;

      if (op === 'has_amenity' || op === 'has_facility') {
        queryValue = item.name;
        field = op === 'has_amenity' ? 'amenities' : 'facilities';
      }
      if (val === '' || val === 'Not Set') {
        op = 'null';
        queryValue = '';
      }
      // Use unified filter: local filters + cross-filters + drilldown
      const merged = mergeFilters(filters, crossFilters);
      const res = await propertyAnalyticsAPI.postDrilldown({
        filters: merged,
        drilldown: { field, value: queryValue, op },
      });
      setDd(prev => ({ ...prev, data: res.data.data || [], loading: false, subtitle: `${res.data.total} properties` }));
    } catch (err) {
      console.error('Drilldown failed:', err);
      setDd(prev => ({ ...prev, data: [], loading: false }));
    }
  }, [filters, crossFilters, mergeFilters]);

  const handleFullView = useCallback(() => {
    const mapping = DRILLDOWN_MAP[dd.key];
    const params = new URLSearchParams();
    // Encode local + cross filters
    const merged = mergeFilters(filters, crossFilters);
    if (merged.state?.length) params.set('state', merged.state.join(','));
    if (merged.city?.length) params.set('city', merged.city.join(','));
    if (merged.property_type?.length) params.set('propertyType', merged.property_type.join(','));
    if (merged.property_status?.length) params.set('propertyStatus', merged.property_status.join(','));
    // Add drilldown filter
    if (mapping) {
      if (mapping.op === 'eq') params.set(mapping.field, dd.val);
      else if (mapping.op === 'has_amenity') params.set('amenity', dd.val);
      else if (mapping.op === 'has_facility') params.set('facility', dd.val);
      else params.set(mapping.field, dd.val);
    }
    navigate(`/properties?${params.toString()}`);
  }, [dd, filters, crossFilters, mergeFilters, navigate]);

  const closeDd = useCallback(() => setDd({ open: false, data: [], title: '', subtitle: '', loading: false, key: '', val: '' }), []);

  const click = (mapKey, label) => (item) => openDd(mapKey, item, label);
  const crossClick = (crossKey, mapKey, label) => (item) => handleChartCrossFilter(crossKey, item, mapKey, label);
  const clickBool = (baseKey, label) => (item) => {
    const k = item.name === 'Yes' ? `${baseKey}_yes` : `${baseKey}_no`;
    openDd(k, item, label);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-60" data-testid="geo-analytics-loading">
        <div className="text-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mx-auto mb-3" /><p className="text-gray-500 text-sm">Loading property analytics...</p></div>
      </div>
    );
  }

  if (!data) return <div className="text-center py-16 text-gray-400">Failed to load property analytics</div>;

  const { kpis, geographical, property_basics, room_inventory, amenities, facilities, banking } = data;
  const rs = room_inventory.stats || {};

  return (
    <div className="space-y-4" data-testid="geo-property-analytics">
      {/* Refresh */}
      <div className="flex justify-end">
        <button onClick={() => load()} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-testid="geo-refresh">
          <RefreshCw size={14} /> Refresh Data
        </button>
      </div>

      {/* Filter Bar */}
      <GeoFilterBar
        filters={filters}
        onChange={setFilters}
        onApply={applyFilters}
        onReset={resetFilters}
        filterOptions={filterOptions}
        activeCount={activeFilterCount}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="geo-kpi-cards">
        <KpiCard title="States Covered" value={kpis.states_covered} icon={MapPin} color="bg-blue-600" />
        <KpiCard title="Cities Covered" value={kpis.cities_covered} icon={Globe2} color="bg-violet-600" />
        <KpiCard title="Total Properties" value={kpis.total_properties} icon={Building2} color="bg-teal-600" />
        <KpiCard title="Total Rooms" value={kpis.total_rooms} icon={Bed} color="bg-emerald-600" />
        <KpiCard title="Avg Rooms" value={kpis.avg_rooms} icon={Home} color="bg-orange-500" sub="Per property" />
        <KpiCard title="With Room Data" value={kpis.properties_with_rooms} icon={Layers} color="bg-pink-500" />
      </div>

      {/* Geographical Distribution */}
      <Section id="geographical" title="Geographical Distribution" icon={MapPin} defaultOpen={true}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4">
          <BarCard data={geographical.by_state.slice(0, 15)} title="Properties by State (Top 15)" onItemClick={crossClick('state', 'state', 'State')} layout="vertical" height={300} color="#3b82f6" />
          <BarCard data={geographical.by_city.slice(0, 15)} title="Properties by City (Top 15)" onItemClick={crossClick('city', 'city', 'City')} layout="vertical" height={300} color="#8b5cf6" />
        </div>
      </Section>

      {/* Property Basics */}
      <Section id="property-basics" title="Property Basics" icon={Building2}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
          <PieCard data={property_basics.property_type} title="Property Type Distribution" onItemClick={click('property_type', 'Property Type')} />
          <PieCard data={property_basics.property_status} title="Property Status" onItemClick={click('property_status', 'Property Status')} />
          <PieCard data={property_basics.star_rating.map(d => ({ ...d, name: d.name === '0' || d.name === 'Not Set' ? 'Unrated' : `${d.name} Star` }))} title="Star Rating" onItemClick={(item) => {
            const val = item.name === 'Unrated' ? '0' : item.name.replace(' Star', '');
            openDd('star_rating', { ...item, name: val }, 'Star Rating');
          }} />
          <PieCard data={property_basics.onboarding_status} title="Onboarding Status" onItemClick={click('onboarding_status', 'Onboarding Status')} />
          <PieCard data={property_basics.cancellation_policy} title="Cancellation Policy" onItemClick={click('cancellation_policy', 'Cancellation Policy')} />
        </div>
      </Section>

      {/* Room Inventory */}
      <Section id="room-inventory" title="Room Inventory" icon={Bed}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pt-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Room Statistics</p>
            <div className="grid grid-cols-2 gap-2">
              <NumStat label="Total Rooms" value={rs.total_rooms} />
              <NumStat label="Avg Rooms" value={rs.avg_rooms} />
              <NumStat label="Max Rooms" value={rs.max_rooms} />
              <NumStat label="With Room Data" value={rs.with_rooms} sub={`of ${kpis.total_properties}`} />
            </div>
          </div>
          <BarCard data={room_inventory.distribution} title="Room Count Distribution" height={180} color="#10b981" />
          <div className="space-y-4">
            <BarCard data={room_inventory.room_types.map(d => ({ name: d.name, count: d.count }))} title="Room Types" height={160} color="#f59e0b" />
            <PieCard data={room_inventory.ac_status} title="Room AC Status" />
          </div>
        </div>
      </Section>

      {/* Amenities & Facilities */}
      <Section id="amenities" title="Amenities & Facilities" icon={Wifi}>
        <div className="pt-4 space-y-6">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">Amenities - Properties with each amenity</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {amenities.map((am, i) => (
                <button key={am.name} onClick={() => openDd('amenity', am, 'Amenity')}
                  className="flex items-center gap-2 bg-gray-50 hover:bg-teal-50 rounded-lg p-3 transition-colors text-left group" data-testid={`geo-amenity-${am.name}`}>
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 group-hover:text-teal-700 truncate">{am.name}</p>
                    <p className="text-lg font-bold text-gray-900">{am.count}</p>
                  </div>
                  <span className="text-[10px] text-gray-400">{data.total > 0 ? Math.round((am.count / data.total) * 100) : 0}%</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">Facilities - Properties with each facility</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {facilities.map((fac, i) => (
                <button key={fac.name} onClick={() => openDd('facility', fac, 'Facility')}
                  className="flex items-center gap-2 bg-gray-50 hover:bg-blue-50 rounded-lg p-3 transition-colors text-left group" data-testid={`geo-facility-${fac.name}`}>
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[(i + 6) % CHART_COLORS.length] }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 group-hover:text-blue-700 truncate">{fac.name}</p>
                    <p className="text-lg font-bold text-gray-900">{fac.count}</p>
                  </div>
                  <span className="text-[10px] text-gray-400">{data.total > 0 ? Math.round((fac.count / data.total) * 100) : 0}%</span>
                </button>
              ))}
            </div>
          </div>
          {/* Combined Top Amenities Bar Chart */}
          <BarCard data={amenities.slice(0, 16)} title="Most Common Amenities" onItemClick={(item) => openDd('amenity', item, 'Amenity')} height={220} color="#0d9488" />
          <BarCard data={facilities.slice(0, 14)} title="Most Common Facilities" onItemClick={(item) => openDd('facility', item, 'Facility')} height={200} color="#3b82f6" />
        </div>
      </Section>

      {/* Banking Completeness */}
      <Section id="banking" title="Banking & Compliance" icon={CreditCard}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
          <BoolStat data={banking.has_bank_name} title="Has Bank Details" onItemClick={clickBool('has_bank', 'Bank Details')} />
          <BoolStat data={banking.has_gst} title="Has GST Number" onItemClick={clickBool('has_gst', 'GST Number')} />
        </div>
      </Section>

      {/* Drilldown Panel */}
      <DrilldownPanel isOpen={dd.open} data={dd.data} title={dd.title} subtitle={dd.subtitle} loading={dd.loading} onClose={closeDd} onFullView={handleFullView} analyticsKey={dd.key} filterValue={dd.val} />
    </div>
  );
};

export default GeoPropertyAnalytics;
